import { Module, createStore } from 'vuex'
import { RootState } from './index'
import {
  ChatMessageBlock, ChatMessageResponse, ChatSession, ChatSessionSummary,
  ChatSessionsSummaryWithDistance, ChatSummaries,
  isChatMessageBlock, isChatMessageRequest, isChatMessageResponse
} from './types/chat';
import {
  DocumentSnapshot, Timestamp, collection, deleteDoc,
  doc, onSnapshot, orderBy, query
} from 'firebase/firestore';
import { db, deleteChatSession } from '../firebase';

export interface ChatState {
  chatSessions?: ChatSession[];
  chatPrivateSessions?: ChatSession[];
  chatSummaries?: ChatSummaries;
  chatPrivateSummaries?: ChatSummaries;
  activeChatMessageBlocks: ChatMessageBlock[];
}

// let unscribeChatSessions: () => void = null;

// スナップショットの解除用
let unsubscribeChatSummaries: (() => void) | undefined = undefined;
let unsubscribeChatPrivateSummaries: (() => void) | undefined = undefined;

const unsubscribeChatMessageBlocks: { [name: string]: (() => void) } = {};

const getAbsolutePath =
  (isPrivate: boolean, rootState: RootState, relativePath: string): string => {
    if (rootState === undefined) throw new Error('rootState is undefined');
    if (rootState.firebase === undefined)
      throw new Error('rootState.firebase is undefined');
    const firebaseState = rootState.firebase;
    const user = firebaseState.user;
    if (user === undefined)
      throw new Error('user is undefined'); // 全員ログインしているのでこれはありえない
    return `${isPrivate ? `users/${user.uid}/` : ''}${relativePath}`;
  };

export const chat: Module<ChatState, RootState> = {
  
  state: {
    chatSessions: undefined,
    chatSummaries: undefined,
    chatPrivateSessions: undefined,
    chatPrivateSummaries: undefined,
    activeChatMessageBlocks: [],
  },

  getters: {

    // chatコレクションのパスを算出する
    userDocPath: (state, getters, rootState) => {
      if (rootState === undefined) return undefined;
      if (rootState.firebase === undefined) return undefined;
      const firebaseState = rootState.firebase;
      const user = firebaseState.user;
      if (user === undefined) return undefined; // 全員ログインしているのでこれはありえない
      return `users/${user.uid}/`;
    }
  },

  mutations: {

    // ChatSummariesをセットする
    setChatSummaries(state, params: { chatSummaries: ChatSummaries, isPrivate?: boolean }) {
      console.log('setChatSummaries init', params, params.chatSummaries);
      const chatSummaries = params.chatSummaries;
      const isPrivate = params.isPrivate ?? false;
      console.log('setChatSummaries chatSummaries', chatSummaries);

      console.log('setChatSummaries [0].title', chatSummaries?.session_summaries[0]?.title,
        'session_id', (chatSummaries?.session_summaries[0] as ChatSessionsSummaryWithDistance)?.distance,
        'isPrivate', isPrivate
      );
      if (isPrivate) {
        console.log('setChatSummaries isPrivate');
        state.chatPrivateSummaries = chatSummaries;
      } else {
        console.log('setChatSummaries not isPrivate', chatSummaries.session_summaries);
        state.chatSummaries = chatSummaries;
      }
    },

    // ChatSummariesをクリアする
    clearChatSummaries(state, isPrivate = false) {
      console.log('clearChatSummaries');
      if (isPrivate) {
        state.chatPrivateSummaries = undefined;
      } else {
        state.chatSummaries = undefined;
      }
    },

    clearActiveChatMessageBlocks(state) {
      console.log('clearActiveChatMessageBlocks');
      const len = state.activeChatMessageBlocks.length;
      for (let i = 0; i < len; i++) {
        state.activeChatMessageBlocks.pop();
        console.log('clearActiveChatMessageBlocks for ', state.activeChatMessageBlocks.length);
      }
    },

    // activeChatMessageBlocksにFirestoreに追加されたChatMessageBlockを連結
    addActiveChatMessageBlocks(state, chatMessageBlocks: ChatMessageBlock[]) {
      console.log('addActiveChatMessageBlocks', chatMessageBlocks);
      if (state.activeChatMessageBlocks) {
        chatMessageBlocks.forEach(block => {
          // state.activeChatMessageBlocks = state.activeChatMessageBlocks.concat(chatMessageBlocks);
          // 
          if (isChatMessageRequest(block) && block.messages[block.messages.length - 1].content.trim() === '') return;
          state.activeChatMessageBlocks.push(block);
        });
      } else {
        state.activeChatMessageBlocks = chatMessageBlocks;
      }
    },

    // activeChatMessageBlocksにFirestoreに更新されたChatMessageBlockを反映
    updateActiveChatMessageBlocks(state, chatMessageBlocks: ChatMessageBlock[]) {
      console.log('updateActiveChatMessageBlocks');
      let updated = false;
      if (state.activeChatMessageBlocks) {
        state.activeChatMessageBlocks = state.activeChatMessageBlocks.map((block) => {
          // チャットセッションが一致し、doc_idが一致するものを更新
          const updatedBlock = chatMessageBlocks.find((b) =>  (b.chat_session_id === block.chat_session_id) && (b.doc_id === block.doc_id));
          // const updatedBlock = chatMessageBlocks.find((b) => (b.chat_session_id === block.chat_session_id) && (b.doc_id === block.doc_id));
          if (updatedBlock) {
            // 同じものが出力されるはず
            // console.log('updated', updatedBlock.doc_id, block.doc_id);
            updated = true;
            return updatedBlock;
          }
          // console.log('not updated', block.doc_id);
          return block;
        });
        // console.log('updateActiveChatMessageBlocks updated', updated);
        if (!updated) {
          // addされたが要素として追加されずに更新された場合があり、その場合はここで追加する
          state.activeChatMessageBlocks.push(...chatMessageBlocks);
        }
      }
    },

    // activeChatMessageBlocksからFirestoreに削除されたChatMessageBlockを削除
    removeActiveChatMessageBlocks(state, chatMessageBlocks: ChatMessageBlock[]) {
      // console.log('removeActiveChatMessageBlocks', chatMessageBlocks);
      if (state.activeChatMessageBlocks) {
        state.activeChatMessageBlocks = state.activeChatMessageBlocks.filter((block) => {
          // 削除対象と一致するもの意外をtruetおして返す
          return !chatMessageBlocks.find((b) =>
            (b.chat_session_id === block.chat_session_id) && (b.doc_id === block.doc_id));
        });
      }
    },
  },

  actions: {

    // Firestoreからチャットセッションの一覧を取得する
    async subscribeChatSessions({ commit, rootState }, isPrivate = false): Promise<() => void> {
      console.log('subscribeChatSessions', isPrivate);
      const docRef = doc(db, getAbsolutePath(isPrivate, rootState, 'chat/summaries'));

      const unsubscribe = onSnapshot(docRef, (doc: DocumentSnapshot) => {
        console.log('subscribeChatSessions onSnapshot', doc.data());
        // 更新があった場合に反映
        commit('setChatSummaries', { chatSummaries: doc.data(), isPrivate });
      });
      if (isPrivate) {
        unsubscribeChatPrivateSummaries = unsubscribe;
      } else {
        unsubscribeChatSummaries = unsubscribe;
      }
      // unsubscribeを返却
      return unsubscribe;
    },

    // 指定したチャットidに近い順にチャットセッション一覧を並べ替える
    async sortChatSessions({ commit, state }, params: { chatSessionId?: string, embedding?: number[], sortBy?: string, isPrivate?: boolean }) {
      const { chatSessionId, embedding, sortBy } = params;
      let isPrivate = params.isPrivate;
      if (isPrivate === undefined) isPrivate = false;
      const chatSummaries = isPrivate ? state.chatPrivateSummaries : state.chatSummaries;
      if (!chatSummaries) {
        console.log('no chatSummaries');
        return;
      }
      const chatSessionSummaries = chatSummaries.session_summaries;
      // 埋め込みベクトルをChatSummariesと同じ順番で取得
      const chatSessionEmbeddings = chatSessionSummaries.map((ChatSessionSummary) => ChatSessionSummary.embedding);
      const chatSessionIds = chatSessionSummaries.map((ChatSessionSummary) => ChatSessionSummary.chat_session.id);
      let targetSessionEmbedding: number[] = [];

      console.log('sortChatSessions chatSessionId', sortBy, chatSessionId);

      if (sortBy === 'time' || chatSessionId) {
        // 指定したチャットidの埋め込みベクトルを取得
        const index = sortBy === 'time' ? -2 : chatSessionIds.indexOf(chatSessionId!);
        // 対象のチャットidが取得できなかった場合は何もしない
        if (index === -1) return;
        // sortByがtimeの場合は時系列に並べ替えて結果を返す
        if (sortBy === 'time' || index === 0 && (chatSessionSummaries[index] as ChatSessionsSummaryWithDistance).distance !== undefined) {
          // 時系列に並べ替えて結果を返す
          const sortedChatSessions = chatSessionSummaries.sort((a: ChatSessionSummary, b: ChatSessionSummary) => {
            return (a.created as Timestamp).seconds - (b.created as Timestamp).seconds;
          });
          chatSessionSummaries.forEach((chatSessionSummary, i) => {
            (chatSessionSummaries[i] as ChatSessionsSummaryWithDistance).distance = undefined;
          });
          commit('setChatSummaries', { chatSummaries: { ...chatSummaries, session_summaries: chatSessionSummaries }, isPrivate });
          return;
        }
        // 対象の埋め込みベクトルを取得
        if (chatSessionEmbeddings[index] === undefined) return;
        targetSessionEmbedding = chatSessionEmbeddings[index]!;
      } else {
        //embeddingが代入されている場合は、それに基づきソート
        if (!embedding) return;
        targetSessionEmbedding = embedding;
      }
      // 対象の埋め込みベクトルが取得できなかった場合は何もしない
      if (!targetSessionEmbedding) return;
      // 埋め込みベクトルの距離を計算
      const distances = chatSessionEmbeddings.map((embedding) => {
        if (!embedding) return 1.0;
        return Math.sqrt(embedding.reduce((acc, cur, i) => {
          return acc + Math.pow(cur - targetSessionEmbedding[i], 2);
        }, 0));
      });
      // distancesを昇順にソートし、chatSummariesのchat_sessionsを並び替える
      let sortedChatSessions = distances.map((distance, i) => {
        const obj: ChatSessionsSummaryWithDistance = { ...chatSessionSummaries[i] };
        obj.distance = distance;
        return obj;
      });
      sortedChatSessions = sortedChatSessions.sort((a, b) => {
        // 昇順にソート
        const distA = a.distance ?? 1;
        const distB = b.distance ?? 1;
        return distB - distA;
      });
      // 並び替えたchat_sessionsをchatSummariesに反映
      console.log('sortedChatSessions', sortedChatSessions);
      commit('setChatSummaries', {
        chatSummaries: {
          ...chatSummaries,
          session_summaries: sortedChatSessions,
        },
        isPrivate,
      });
    },

    // Firestoreから特定セッションのChatMessageBlocksを取得し更新を監視する
    async subscribeChatMessageBlocks({ commit, rootState }, params: { chatSessionId: string, isPrivate: boolean }): Promise<() => void> {
      const { chatSessionId } = params;
      let isPrivate = params.isPrivate;
      if (isPrivate === undefined) isPrivate = false;
      console.log('subscribeChatMessageBlocks ', chatSessionId, isPrivate);
      if (unsubscribeChatMessageBlocks[chatSessionId]) {
        unsubscribeChatMessageBlocks[chatSessionId]();
      }
      commit('clearActiveChatMessageBlocks');

      const colRef = collection(db, getAbsolutePath(isPrivate, rootState, `chat/data/sessions/${chatSessionId}/message_blocks`));
      const q = query(colRef, orderBy('created', 'asc'));

      unsubscribeChatMessageBlocks[chatSessionId] = onSnapshot(q, (querySnapshot) => {
        console.log('onSnapshot', querySnapshot);
        // 更新があった場合に反映
        const addedMessageBlocks: ChatMessageBlock[] = [];
        const updatedMessageBlocks: ChatMessageBlock[] = [];
        const removedMessageBlocks: ChatMessageBlock[] = [];

        querySnapshot.docChanges().forEach((change) => {
          console.log('change', change);
          const docData = change.doc.data();
          docData.doc_id = change.doc.id;
          if (isChatMessageBlock(docData)) {
            switch (change.type) {
              case 'added':
                console.log('added', docData);
                // addedMessageBlocks.push(docData as ChatMessageBlock);
                updatedMessageBlocks.push(docData as ChatMessageBlock);
                break;
              case 'modified':
                console.log('modified', change.doc.data());
                updatedMessageBlocks.push(docData as ChatMessageBlock);
                break;
              case 'removed':
                console.log('removed', change.doc.data());
                removedMessageBlocks.push(docData as ChatMessageBlock);
                break;
              default:
                console.log('change.type is unknown', change.type);
            }
          }
        });
        // commit('addActiveChatMessageBlocks', addedMessageBlocks); // 追加中に更新があった場合、空になることがある
        // リアルタイムで更新を反映しているように見せかけるため、１文字ごとに更新を反映する
        updatedMessageBlocks.forEach(async (updatedMessageBlock) => {
          // 現在のメッセージブロックを取得
          const currentMessageBlock = rootState.chat.activeChatMessageBlocks.find((messageBlock) => {
            return messageBlock.doc_id === updatedMessageBlock.doc_id;
          });
          if (!currentMessageBlock) {
            commit('addActiveChatMessageBlocks', [updatedMessageBlock]);
            return;
          }
          if (!isChatMessageResponse(currentMessageBlock)) {
            // メッセージリクエストの場合は、一括で反映
            commit('updateActiveChatMessageBlocks', [updatedMessageBlock]);
            return;
          }
          // 元のメッセージサイズを取得
          const currentIndex = 
            (currentMessageBlock as ChatMessageResponse)
              .choices[0].message.content.length;

          const newMessageBlock = Object.assign(
            {},
            (updatedMessageBlock as ChatMessageResponse));
          
          const newContent = newMessageBlock.choices[0].message.content;

          const sleep = (waitTime: number) => new Promise( resolve => setTimeout(resolve, waitTime) );

          // 3文字ずつ更新を反映
          // for (let i = 0; i < newContent.length - currentIndex + 1; i++) {
          //     newMessageBlock.choices[0].message.content = newContent.substring(0, currentIndex + i);
          //     commit('updateActiveChatMessageBlocks', [newMessageBlock]);
          //     await sleep( 1000 / (newContent.length - currentIndex));
          // }
          commit('updateActiveChatMessageBlocks', [updatedMessageBlock]);
        });
        commit('removeActiveChatMessageBlocks', removedMessageBlocks);
      });
      // unsubscribeを返却
      return unsubscribeChatMessageBlocks[chatSessionId];
    },

    // ChatMessageBlockを削除する
    async deleteChatMessageBlock({ commit, rootState }, params: { chatMessageBlock: ChatMessageBlock, isPrivate: boolean }): Promise<void> {
      const { chatMessageBlock } = params;
      const isPrivate = params.isPrivate ?? false;
      console.log('deleteChatMessageBlock', chatMessageBlock, isPrivate);
      if (!chatMessageBlock) return;
      const docRef = doc(db,
        getAbsolutePath(
          isPrivate, rootState,
          `chat/data/sessions/${chatMessageBlock.chat_session_id}/message_blocks/${chatMessageBlock.doc_id}`));
      try {
        await deleteDoc(docRef);
      } catch (e) {
        console.error('deleteChatMessageBlock', e);
        throw e;
      }
    },

    // ChatSessionを削除する
    async deleteChatSession({ rootState }, params: { chatSessionSummary: ChatSessionSummary, isPrivate: boolean }): Promise<void> {
      console.log('deleteChatSession', params);
      const { chatSessionSummary } = params;
      const isPrivate = params.isPrivate ?? false;

      if (!chatSessionSummary) return;
      try {
        const result = await deleteChatSession(
          {
            chat_session_id: chatSessionSummary.chat_session.id,
            basePath: getAbsolutePath(isPrivate, rootState, '')
          }
        );
        console.log('deleteChatMessageBlock', result);
      } catch (e) {
        console.error('deleteChatMessageBlock', e);
        throw e;
      }
    }
  },

  modules: {
  }
}
